import { UpdateResolver } from '@urql/exchange-graphcache';
import {
  AllocateProjectCreditsMutation,
  AllocateProjectCreditsMutationVariables,
  ConsumeProjectCreditsMutation,
  ConsumeProjectCreditsMutationVariables,
  DeallocateProjectCreditsMutation,
  DeallocateProjectCreditsMutationVariables,
} from '../../gql/graphql';

/**
 * Invalidate all credit-related queries after a credit mutation.
 * This ensures the UI reflects the latest credit state.
 * Exported so it can be used by other resolvers (e.g., order creation triggers credit consumption).
 */
export function invalidateCreditQueries(cache: Parameters<UpdateResolver>[2]) {
  // Invalidate tenant credits
  cache
    .inspectFields('Query')
    .filter((field) => field.fieldName === 'myTenantCredits')
    .forEach((field) => {
      cache.invalidate('Query', field.fieldKey);
    });

  // Invalidate project credits
  cache
    .inspectFields('Query')
    .filter((field) => field.fieldName === 'myProjectCredits')
    .forEach((field) => {
      cache.invalidate('Query', field.fieldKey);
    });

  // Invalidate credits summary
  cache
    .inspectFields('Query')
    .filter((field) => field.fieldName === 'myCreditsSummary')
    .forEach((field) => {
      cache.invalidate('Query', field.fieldKey);
    });

  // Invalidate project credit allocations
  cache
    .inspectFields('Query')
    .filter((field) => field.fieldName === 'myProjectCreditAllocations')
    .forEach((field) => {
      cache.invalidate('Query', field.fieldKey);
    });

  // Invalidate credit consumptions
  cache
    .inspectFields('Query')
    .filter((field) => field.fieldName === 'myCreditConsumptions')
    .forEach((field) => {
      cache.invalidate('Query', field.fieldKey);
    });

  // Invalidate hasCreditsAvailable checks
  cache
    .inspectFields('Query')
    .filter((field) => field.fieldName === 'hasCreditsAvailable')
    .forEach((field) => {
      cache.invalidate('Query', field.fieldKey);
    });

  // Invalidate credit packs
  cache
    .inspectFields('Query')
    .filter((field) => field.fieldName === 'myCreditPacks')
    .forEach((field) => {
      cache.invalidate('Query', field.fieldKey);
    });
}

export const allocateProjectCreditsResolver: UpdateResolver = (
  result: AllocateProjectCreditsMutation,
  _args: AllocateProjectCreditsMutationVariables,
  cache
) => {
  if (!result.allocateProjectCredits) return;
  invalidateCreditQueries(cache);
};

export const deallocateProjectCreditsResolver: UpdateResolver = (
  result: DeallocateProjectCreditsMutation,
  _args: DeallocateProjectCreditsMutationVariables,
  cache
) => {
  if (!result.deallocateProjectCredits) return;
  invalidateCreditQueries(cache);
};

export const consumeProjectCreditsResolver: UpdateResolver = (
  result: ConsumeProjectCreditsMutation,
  _args: ConsumeProjectCreditsMutationVariables,
  cache
) => {
  if (!result.consumeProjectCredits) return;
  invalidateCreditQueries(cache);
};
